/**
 * Refined Design — header language menu.
 *
 * Drives the drawn dropdown in snippets/localization-form.liquid. It never
 * submits anything itself: a pick writes the value into the form's hidden
 * <select> and fires its change event, so localization.js switches the
 * language exactly as it does for the native control.
 */
class RdLangMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.rd-lang');
    this.list = this.querySelector('.rd-lang__list');
    this.select = this.closest('.language-selector')?.querySelector('select');
    if (!this.button || !this.list || !this.select) return;

    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.button.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    this.button.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      }
    });
    this.list.addEventListener('click', (event) => {
      const option = event.target.closest('.rd-lang__option');
      if (option) this.pick(option);
    });
    this.list.addEventListener('keydown', (event) => this.onListKey(event));
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutside);
  }

  get isOpen() {
    return !this.list.hidden;
  }

  get options() {
    return [...this.list.querySelectorAll('.rd-lang__option')];
  }

  open() {
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.onOutside);
    const current = this.options.find((o) => o.getAttribute('aria-selected') === 'true');
    (current || this.options[0])?.focus();
  }

  close({ refocus = false } = {}) {
    if (!this.isOpen) return;
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onOutside);
    if (refocus) this.button.focus();
  }

  onListKey(event) {
    const options = this.options;
    const index = options.indexOf(document.activeElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        options[(index + 1) % options.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        options[(index - 1 + options.length) % options.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (index > -1) this.pick(options[index]);
        break;
      case 'Escape':
        event.preventDefault();
        this.close({ refocus: true });
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  pick(option) {
    const value = option.dataset.value;
    this.close({ refocus: true });
    if (!value || value === this.select.value) return;
    this.select.value = value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('rd-lang-menu')) customElements.define('rd-lang-menu', RdLangMenu);
